import { useEffect } from 'react'

// material-ui
import { Button, Chip } from '@mui/material'
import { PlusCircleOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import useMediaQuery from '@mui/material/useMediaQuery'

// project import
import { useAuth } from 'services/hook/Auth'
import { useApiData } from 'services/hook/useApiData'
import { useAuthContext } from 'services/provider/AuthProvider'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import CellRenderer from 'components/list/CellRenderer'
import { getPermissionKeys } from 'utils/util'
import { sanitizeFilters } from 'utils/helpers'
import { bannerList } from 'models/banner'
import { EVENT_BANNER_ADD_INIT, EVENT_BANNER_DELETE_INIT, EVENT_BANNER_DETAIL_INIT, EVENT_BANNER_UPDATE_INIT, EVENT_BANNER_UPDATE_SUCCESS } from 'models/events/banner.event'
import { BannerAddDialog } from './Banner.Create'
import { BannerEditDialog } from './Banner.Update'
import { BannerDetailDialog } from './Banner.Detail'
import { BannerDeleteDialog } from './Banner.Delete'
import DataGridList from 'components/data-grid/DataGridList'
import { labelFor, BANNER_VIEW_TYPES, BANNER_PAGE_TYPES } from 'constants/banner'
import { CellMobileDetails } from './list/Banner.List.Cell'

const BannerList = () => {
    const permissionsKeys = getPermissionKeys('banner')
    return (
        <>
            <PageHeader title="Banners" breadcrumbs={[{ path: '#', label: 'Banners' }]} />
            <BannerListTable permissionsKeys={permissionsKeys} />
            {permissionsKeys.can_create ? <BannerAddDialog /> : null}
            {permissionsKeys.can_update ? <BannerEditDialog /> : null}
            {permissionsKeys.can_view ? <BannerDetailDialog /> : null}
            {permissionsKeys.can_delete ? <BannerDeleteDialog /> : null}
        </>
    )
}

const CellStatus = ({ item }) => {
    const cellFontSize = { xs: 18, xl: 22 }
    const isActive = item.status === 'active' || item.status === 1 || item.status === true
    const bgColor = isActive ? '#e2ffc8' : '#f9e3e1'
    const fgColor = isActive ? '#389E0D' : '#CF1322'
    const label = isActive ? 'Active' : 'Inactive'
    const icon = isActive ? <CheckCircleOutlined sx={{ fontSize: cellFontSize }} style={{ color: fgColor }} /> : <CloseCircleOutlined sx={{ fontSize: cellFontSize }} style={{ color: fgColor }} />

    return (
        <Chip
            label={label}
            size="medium"
            icon={icon}
            sx={{
                mt: '5px',
                color: fgColor,
                backgroundColor: bgColor,
                fontSize: { xs: 14, xl: 15 }
            }}
        />
    )
}

const BannerListTable = ({ permissionsKeys }) => {
    const { signOut } = useAuthContext()
    const eventbus = useEventBusContext()
    const initdata = { data: [], total: 0 }
    const initparams = { page: 0, limit: 10, sort: '', orderby: '', filters: {} }
    const is_mobile = useMediaQuery('(max-width:991px)')

    const colheadsx = {
        '& .MuiDataGrid-cellCheckbox': { alignItems: 'center' },
        '& .MuiDataGrid-topContainer': { opacity: 0, visibility: 'hidden' },
        '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #c5c5c5a1 !important',
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const desktopGridSx = {
        '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            lineHeight: 1.5,
            outline: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-row': {
            minHeight: '48px !important',
            borderBottom: '1px solid #e0e0e0 !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-cellContent': {
            fontSize: '0.875rem'
        },
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '1rem',
            fontWeight: 600,
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '1rem',
            fontWeight: 600,
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const [data, params, loading, loadData] = useApiData(initdata, initparams, (params, setData) => {
        const sanitizedFilters = sanitizeFilters(params.filters || {})
        const apiParams = { ...params, ...sanitizedFilters, page: (params.page || 0) + 1 }
        bannerList(apiParams)
            .then((res) => setData(res))
            .catch((res) => {
                if (res?.status_code === 401) signOut()
            })
    }, false)

    useEffect(() => {
        return eventbus.on(EVENT_BANNER_UPDATE_SUCCESS, () => loadData(params))
    }, [eventbus, params, loadData])

    const getBannerPropsData = () => ({
        actions: {
            detail: { tooltip: 'View Details', menu: 'View Details', icon: <EyeOutlined />, event: EVENT_BANNER_DETAIL_INIT },
            update: { tooltip: 'Edit Banner', menu: 'Edit', icon: <EditOutlined />, event: EVENT_BANNER_UPDATE_INIT },
            delete: { tooltip: 'Delete Banner', menu: 'Delete', icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />, event: EVENT_BANNER_DELETE_INIT }
        },
        events: {
            detail_init: EVENT_BANNER_DETAIL_INIT,
            update_init: EVENT_BANNER_UPDATE_INIT,
            delete_init: EVENT_BANNER_DELETE_INIT
        }
    })

    const mobile_cols = [
        { field: 'name', headerName: 'Detail', flex: 1, renderCell: (props) => (<CellMobileDetails item={props.row} propsDetails={getBannerPropsData()} />) }
    ]

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150, renderCell: (props) => (<CellRenderer type="name" item={props.row} propsDetails={getBannerPropsData()} />) },
        { field: 'view_type', headerName: 'View Type', flex: 0.8, minWidth: 120, renderCell: (props) => labelFor(BANNER_VIEW_TYPES, props.row.view_type) },
        { field: 'page_type', headerName: 'Page Type', flex: 0.8, minWidth: 120, renderCell: (props) => labelFor(BANNER_PAGE_TYPES, props.row.page_type) },
        { field: 'status', headerName: 'Status', flex: 0.6, minWidth: 100, renderCell: (props) => (<CellStatus item={props.row} />) },
        { field: 'action', headerName: 'Action', sortable: false, flex: 0.5, minWidth: 150, maxWidth: 150, renderCell: (props) => (<CellRenderer type="action" item={props.row} propsDetails={getBannerPropsData()} />) }
    ]

    return (
        <DataGridList
            rows={data.data}
            columns={!is_mobile ? columns : mobile_cols}
            getRowHeight={() => 'auto'}
            actions={<ListActions />}
            loading={loading}
            filterkey="bannerlist"
            sx={!is_mobile ? desktopGridSx : colheadsx}
            search={(query) => loadData({ ...params, query })}
            rowCount={data.total}
            paginationMode="server"
            paginationModel={{ page: params.page, pageSize: params.limit }}
            onPaginationModelChange={(pagination) => loadData({ ...params, page: pagination.page, limit: pagination.pageSize })}
            sortingMode="server"
            onSortModelChange={(sortModel) =>
                sortModel.length > 0
                    ? loadData({ ...params, orderby: sortModel[0].field, sort: sortModel[0].sort })
                    : loadData({ ...params, orderby: '', sort: '' })
            }
            onInit={(pagination, sortModel, search, filters) => {
                let inparam = { query: search, filters }
                if (pagination !== undefined) inparam = { ...inparam, page: pagination.page, limit: pagination.pageSize }
                if (sortModel !== undefined && sortModel.length > 0) inparam = { ...inparam, orderby: sortModel[0].field, sort: sortModel[0].sort }
                loadData({ ...params, ...inparam })
            }}
        />
    )
}

const ListActions = () => {
    const eventbus = useEventBusContext()
    const { checkPermission } = useAuth()
    return (
        <>
            {checkPermission('banner.create') ? (
                <Button variant="contained" onClick={() => eventbus.fire(EVENT_BANNER_ADD_INIT)} endIcon={<PlusCircleOutlined />}>
                    Add Banner
                </Button>
            ) : null}
        </>
    )
}

export default BannerList